/**
 * Supabase Client Singleton
 *
 * Environment-aware Supabase client with app-scoped storage
 * to prevent cross-app contamination
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js'
import type { Database } from './database.types'

// App-specific storage keys to prevent cross-app contamination
export type AppType = 'customer' | 'restaurant' | 'admin'

/**
 * Get Supabase URL from environment or use default staging URL
 */
const getSupabaseUrl = (): string => {
  // Check for explicit override first
  const override = typeof window !== 'undefined'
    ? (import.meta as any).env?.VITE_SUPABASE_URL
    : process.env.VITE_SUPABASE_URL

  if (override) return override

  // Default staging URL
  return 'https://tjgayvnaqzksmsiekbib.supabase.co'
}

/**
 * Get Supabase Anon Key from environment
 *
 * IMPORTANT: The anon key is PUBLIC and safe to expose.
 * It's designed to be used in client-side applications.
 */
const getSupabaseAnonKey = (): string => {
  // Check for explicit override
  const override = typeof window !== 'undefined'
    ? (import.meta as any).env?.VITE_SUPABASE_ANON_KEY
    : process.env.VITE_SUPABASE_ANON_KEY

  if (override) return override

  // This will need to be set via environment variables
  throw new Error(
    'VITE_SUPABASE_ANON_KEY is not set. ' +
    'Please add it to your .env file or environment variables.'
  )
}

/**
 * Create a Supabase client with app-scoped storage
 *
 * @param appType - The type of app (customer, restaurant, admin)
 * @returns Configured Supabase client
 */
export const createSupabaseClient = (appType: AppType): SupabaseClient<Database> => {
  return createClient<Database>(
    getSupabaseUrl(),
    getSupabaseAnonKey(),
    {
      auth: {
        persistSession: true,
        // IMPORTANT: Use unique key to avoid conflict with Zustand persist middleware
        // which uses 'rsvd-${appType}-auth' and would overwrite session data
        storageKey: `supabase-${appType}-session`,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
      realtime: {
        params: {
          eventsPerSecond: 10
        }
      },
      global: {
        headers: {
          'X-Client-Info': `rsvd-${appType}-app`
        }
      }
    }
  )
}

// NOTE: Default supabase export was REMOVED to prevent "Multiple GoTrueClient instances" issue.
// Each app should create its own client via createSupabaseClient('customer'|'restaurant'|'admin')
// in their local lib/supabase.ts file.
